import { useState, useEffect, useCallback, useRef } from 'react';
import { B, space, font, radius, weight } from '../constants/brand';
import { api } from '../api/client';
import { useApi } from '../hooks/useApi';
import { Card, Btn, Spinner, ErrorMsg, Badge, Icon, InfoNote, EmptyState, PageHeader, useToast } from '../components/ui';
import { InlineSearchPicker, useDealPickerItems } from '../components/tasks/TaskComponents';
// Mirrors api/src/docMigrationRun.js — KEEP THE TWO IN LOCKSTEP. The server writes
// the checkpoint, this reads it.
import { parseRunProgress, runFraction } from '../utils/docMigrationRun';

// ============================================================================
// DocMigration — Neil-only bulk document migration.
//
// Source is a dedicated "Neil Dropbox" folder on the Harvus Documents
// SharePoint site. Neil copies his existing folders into it in SharePoint, then:
//   1. Rescan reads that folder and lists each top-level sub-folder.
//   2. Map each sub-folder to the Deal / DealDetail it belongs to — every deal
//      detail is offered, including the lone detail of a single-detail deal
//      (see useDealPickerItems).
//   3. Import the new files — anything already in that deal (same name + size)
//      is skipped. Imported files run through the normal OCR + SharePoint sync.
//
// The scan is split in two so no single request has to walk the whole tree
// (which blew past the Static Web Apps edge timeout on a large Dropbox):
//   • GET /doc-migration/scan         — light: just the top-level folder list.
//   • GET /doc-migration/scan-folder  — per-folder detail (files + dedupe),
//                                        loaded lazily by each FolderRow below.
//
// The nav entry is gated to ngodfrey@harvus.com; every endpoint here is also
// gated server-side, so this page simply 401s for anyone else.
// ============================================================================

// ── Scan concurrency limiter ────────────────────────────────────────────────
//
// Every FolderRow scans itself on mount, and each of those scans runs its own
// concurrent Graph tree walk server-side. Unbounded, a page with 15 folders
// fired 15 simultaneous scans — enough to trip Graph's per-app throttle and
// fail the lot with `activityLimitReached`. So the rows queue through here:
// a couple at a time, which is still comfortably faster than sequential.
const MAX_CONCURRENT_SCANS = 2;
let activeScans = 0;
const scanQueue = [];

function releaseScanSlot() {
  activeScans--;
  const next = scanQueue.shift();
  if (next) { activeScans++; next(); }
}

function acquireScanSlot() {
  if (activeScans < MAX_CONCURRENT_SCANS) { activeScans++; return Promise.resolve(); }
  return new Promise(resolve => scanQueue.push(resolve));
}

// Run `fn` holding a scan slot. Always releases, so one failure can't wedge the
// queue and leave the remaining folders stuck on "Scanning…".
async function withScanSlot(fn) {
  await acquireScanSlot();
  try { return await fn(); }
  finally { releaseScanSlot(); }
}

function humanSize(bytes) {
  if (bytes == null) return '—';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  let n = bytes, i = 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}

const STATUS_META = {
  new:       { label: 'New',            color: B.green },
  duplicate: { label: 'Already in app', color: B.textLt },
  unmapped:  { label: 'Unmapped',       color: B.amber },
};

export default function DocMigration() {
  const { toast } = useToast();
  const { dealPickerItems, dealPickerError } = useDealPickerItems();
  // Light scan — just the top-level folder list. Fast, so the default timeout
  // is plenty; each folder's files/dedupe load separately (see FolderRow).
  const { data: scan, loading, error, reload } = useApi(
    () => api.get('doc-migration/scan', { timeout: 35000 }), []);

  const folders     = scan?.folders || [];
  const dropboxName = scan?.sourceFolder || 'Neil Dropbox';

  // Each FolderRow reports its "new files" count up here as it finishes its own
  // lazy scan, so we can show a running total across all folders.
  const [newByFolder, setNewByFolder] = useState({});
  const onStats = useCallback(
    (sourceFolder, newCount) => setNewByFolder(s => ({ ...s, [sourceFolder]: newCount })),
    []);
  const totalNew = Object.values(newByFolder).reduce((a, b) => a + (b || 0), 0);

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <PageHeader
        title="Document Migration"
        subtitle={`Import documents from the "${dropboxName}" SharePoint folder, mapped folder-by-folder to deals.`}
        icon="documents"
        actions={
          <div style={{ display: 'flex', gap: space.sm }}>
            {scan?.sourceWebUrl && (
              <Btn variant="secondary" onClick={() => window.open(scan.sourceWebUrl, '_blank', 'noopener')}>
                Open in SharePoint
              </Btn>
            )}
            <Btn onClick={reload} disabled={loading}>Rescan</Btn>
          </div>
        }
      />

      {scan && scan.configured === false && (
        <div style={{ marginBottom: space.lg }}>
          <ErrorMsg msg="SharePoint isn't configured on this environment, so the migration source is unavailable." />
        </div>
      )}

      {dealPickerError && (
        <div style={{ marginBottom: space.lg }}>
          <ErrorMsg msg={`Couldn't load the deal list, so the mapping pickers below are empty: ${dealPickerError}`} />
        </div>
      )}

      <Card style={{ marginBottom: space.lg }}>
        <div style={{ fontWeight: weight.semibold, color: B.text, marginBottom: space.xs }}>How this works</div>
        <ol style={{ margin: 0, paddingLeft: 18, color: B.textMd, fontSize: font.sm, lineHeight: 1.7 }}>
          <li>
            In SharePoint, copy your documents into the <strong>“{dropboxName}”</strong> folder on the Harvus
            Documents site{scan?.sourceWebUrl ? ' (use “Open in SharePoint” above)' : ''}. Put each set of docs in
            its own sub-folder — one sub-folder per deal.
          </li>
          <li>
            Back here, hit <strong>Rescan</strong>, then <strong>map</strong> each sub-folder to the deal it
            belongs to — or to a specific <strong>deal detail</strong> under it (the indented rows in the
            picker), which files the documents against that detail instead of the deal-level bucket.
          </li>
          <li><strong>Import</strong> the new files. Anything already in that deal (same name &amp; size) is skipped automatically.</li>
        </ol>
        <InfoNote>
          On import, each file is auto-sorted by AI into the deal’s standard folders (Legal, Financial, Reports, …)
          based on its name; anything the AI can’t place confidently keeps its original source folder. Set a
          “Target sub-folder” below to override AI sorting and file everything there instead.
          Files stay in the Dropbox folder after importing — use “Clear already-imported” or “Remove from Dropbox”
          to tidy up once a deal looks right. Importing copies each file into the deal and runs it through OCR.
        </InfoNote>
      </Card>

      {loading && !scan ? (
        <Spinner label="Reading the Dropbox folder…" />
      ) : error ? (
        <ErrorMsg msg={error} onRetry={reload} />
      ) : folders.length === 0 ? (
        <EmptyState icon="documents" title={`The "${dropboxName}" folder is empty`}
          message="Add sub-folders of documents to it in SharePoint, then hit Rescan." />
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: space.sm }}>
            <div style={{ fontWeight: weight.semibold, color: B.text }}>
              {folders.length} folder{folders.length === 1 ? '' : 's'} found
            </div>
            {totalNew > 0 && <div style={{ color: B.textMd, fontSize: font.sm }}>{totalNew} new file{totalNew === 1 ? '' : 's'} ready to import</div>}
          </div>
          {folders.map((f) => (
            <FolderRow key={f.sourceFolder} folder={f} dealPickerItems={dealPickerItems}
                       toast={toast} reloadList={reload} onStats={onStats} />
          ))}
        </>
      )}
    </div>
  );
}

// ── One source sub-folder: lazily scan it, map it, then import its new files ──
function FolderRow({ folder, dealPickerItems, toast, reloadList, onStats }) {
  const enc = encodeURIComponent(folder.sourceFolder);

  // Per-folder detail (files + dedupe), fetched on mount and after any action,
  // so the heavy walk is scoped to this one folder rather than the whole tree.
  const [detail, setDetail]         = useState(null);
  const [detailErr, setDetailErr]   = useState(null);
  const [loadingDetail, setLoading] = useState(true);

  // `fresh` bypasses the server's short-lived walk cache. The automatic scan on
  // mount happily uses the cache (that is the point — a page load no longer
  // re-walks every folder over Graph); anything the user explicitly triggers, or
  // that follows a change to the source, asks for a fresh walk.
  const loadDetail = useCallback(async ({ fresh = false } = {}) => {
    // A fresh scan deliberately discards the server's folder cache, so the
    // convergence attempts start over rather than counting against the old ones.
    if (fresh) partialTries.current = 0;
    setLoading(true); setDetailErr(null);
    try {
      const d = await withScanSlot(() =>
        api.get(`doc-migration/scan-folder?sourceFolder=${enc}${fresh ? '&refresh=1' : ''}`,
          { timeout: 60000 }));
      setDetail(d);
      onStats?.(folder.sourceFolder, d.newCount || 0);
    } catch (e) {
      setDetailErr(e.message);
      onStats?.(folder.sourceFolder, 0);
    } finally { setLoading(false); }
  }, [enc, folder.sourceFolder, onStats]);

  useEffect(() => { loadDetail(); }, [loadDetail]);   // cached walk is fine here

  // A partial scan converges by itself. The server caches each FOLDER listing it
  // manages to read, so a follow-up scan costs no Graph calls for ground already
  // covered and spends its whole budget on new folders — successive attempts get
  // further. Without this the folder would simply sit at "still reading" until
  // someone pressed ↻, which busts that cache and starts it over.
  const MAX_PARTIAL_TRIES = 6;
  useEffect(() => {
    if (!detail?.partial) { partialTries.current = 0; return; }
    if (partialTries.current >= MAX_PARTIAL_TRIES) return;
    const id = setTimeout(() => {
      partialTries.current += 1;
      loadDetail();                 // NOT fresh — the point is to reuse the cache
    }, 2000);
    return () => clearTimeout(id);
  }, [detail, loadDetail]);


  // Mapping comes from the fresh detail once loaded, else the light scan's copy.
  const mapping = detail?.mapping ?? folder.mapping;
  const initialValue = mapping
    ? (mapping.dealDetailID ? `detail:${mapping.dealDetailID}` : `deal:${mapping.dealID}`)
    : '';

  const [pickValue, setPickValue]   = useState(initialValue);
  const [target, setTarget]         = useState(mapping?.targetFolderPath || '');
  const [expanded, setExpanded]     = useState(false);
  const [saving, setSaving]         = useState(false);
  const [importing, setImporting]   = useState(false);
  // The LAST import failure, kept on screen. A toast disappears before anyone can
  // read or copy it, and the server now returns a `step` plus the real cause —
  // that is the one thing worth not losing when an import fails.
  const [importErr, setImportErr]   = useState(null); // { message, step, rawError }
  // Per-file failures from the last run. These used to go only to console.warn,
  // which hides exactly the actionable ones (e.g. a file over the size limit).
  const [fileErrs, setFileErrs]     = useState([]);
  // The server-side run: what it is doing now, and whether we are watching it.
  const [run, setRun]               = useState(null);
  const [polling, setPolling]       = useState(false);
  // A failure from the start request, held back until the poller can say whether
  // a run actually exists. See runImport for why.
  const startErr                    = useRef(null);
  // Automatic follow-up scans while a folder is still only partially indexed.
  // Bounded, and reset whenever a scan completes or the user asks for a fresh one.
  const partialTries                = useRef(0);
  // Poll the run while one is live. Deliberately unbounded in ticks (a 17 GB
  // import legitimately takes many minutes) but terminated by the server: the
  // status endpoint flips a run whose invocation died to `error`, so this always
  // reaches a real outcome rather than spinning forever.
  useEffect(() => {
    if (!polling) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get(
          `doc-migration/ingest/status?sourceFolder=${enc}`, { timeout: 20000 });
        if (cancelled) return;
        setRun(r.run || null);
        if (r.run?.errors?.length) setFileErrs(r.run.errors);
        // A run exists, so whatever the start request answered is irrelevant.
        if (r.run) startErr.current = null;

        const done = !r.run || ['done', 'error', 'cancelled'].includes(r.run.status);
        if (done) {
          setPolling(false);
          setImporting(false);
          if (r.run?.status === 'error') {
            setImportErr({ message: r.run.message, step: null, rawError: null });
          } else if (r.run?.status === 'done') {
            toast.success(r.run.message || 'Import finished.');
          } else if (!r.run && startErr.current) {
            // No run was ever created — the start really did fail.
            setImportErr(startErr.current);
            startErr.current = null;
          }
          await loadDetail();          // counts moved
        }
      } catch { /* a failed poll is not a failed run — try again next tick */ }
    };
    tick();
    const id = setInterval(tick, 4000);
    return () => { cancelled = true; clearInterval(id); };
  }, [polling, enc, loadDetail]);

  // Re-attach on mount to a run already going (another tab, or this page
  // reloaded). The whole point of moving the schedule server-side is that the
  // import does not belong to one browser tab any more.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`doc-migration/ingest/status?sourceFolder=${enc}`);
        if (cancelled || !r.run) return;
        if (!['done', 'error', 'cancelled'].includes(r.run.status)) {
          setRun(r.run); setImporting(true); setPolling(true);
        }
      } catch { /* nothing to re-attach to */ }
    })();
    return () => { cancelled = true; };
  }, [enc]);

  // The run's own view of itself. `phase` is the checkpoint it last wrote, so the
  // user sees the stage it is actually in — including "asking Claude where each
  // file belongs", which is now allowed to take as long as it needs.
  const runLive    = !!run && !['done', 'error', 'cancelled'].includes(run.status);
  const runPhase   = parseRunProgress(run?.message);
  const runFrac    = runFraction(run);
  const runSummary = runLive
    ? [runPhase?.label, runPhase?.detail].filter(Boolean).join(' — ') || 'Starting…'
    : '';

  const isMapped  = !!(mapping && (mapping.dealID || mapping.dealDetailID));
  const fileCount = detail?.fileCount ?? null;
  const newCount  = detail?.newCount || 0;
  const dupCount  = detail?.dupCount || 0;
  // The server could not walk the whole source tree inside its request budget,
  // so `files`/`fileCount` are what it managed to index — not the full folder.
  const partial   = !!detail?.partial;

  const saveMapping = async (value, targetFolderPath) => {
    const item = dealPickerItems.find(i => i.value === value);
    if (!item) return;
    setSaving(true);
    try {
      await api.put('doc-migration/mappings', {
        sourceFolder:     folder.sourceFolder,
        dealId:           item.dealDetailID ? null : item.dealID,
        dealDetailId:     item.dealDetailID || null,
        targetFolderPath: targetFolderPath || null,
      });
      await loadDetail();   // remapping changes which files count as new/dupe
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const clearMapping = async () => {
    setSaving(true);
    try {
      await api.del(`doc-migration/mappings?sourceFolder=${enc}`);
      setPickValue('');
      await loadDetail();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  // Start a SERVER-SIDE run and watch it, rather than looping the import from
  // here. The browser used to be the scheduler: it called /ingest over and over
  // until `remaining` hit zero, which meant closing the tab abandoned the import
  // and every stage had to fit inside the ~45s edge window — including Claude's
  // filing, which had to be time-boxed to ~12s purely because someone was
  // waiting. The run does the laps server-side and a timer resumes it if its
  // invocation dies, so this only starts it and polls.
  const runImport = async () => {
    setImporting(true);
    setImportErr(null);
    setFileErrs([]);
    try {
      // Deliberately NOT awaited for its result. The start request drives the
      // first laps inline (Azure reclaims the instance once a response is
      // written, so there is no "respond early and keep working"), which means it
      // routinely outlasts the ~45s edge — and when it does, the edge answers with
      // a BODYLESS 500. That is expected for this endpoint, not a failure: the
      // run's state lives in the DB.
      //
      // So a failure here is only RECORDED, never shown. The poller decides: if a
      // run exists, this is discarded; if no run ever appears, it is surfaced,
      // because then the start genuinely did not take. Showing it immediately is
      // what put a red "Import failed" over a run that was importing fine.
      api.post('doc-migration/ingest/start', { sourceFolder: folder.sourceFolder },
        { timeout: 300000 })
        .catch((e) => {
          startErr.current = { message: e.message, step: e.body?.step || null,
                               rawError: e.body?.rawError || null };
        });
      // Give the row a moment to exist, then hand over to the poller.
      await new Promise(r => setTimeout(r, 800));
      setPolling(true);
    } catch (e) {
      toast.error(e.message);
      setImporting(false);
    }
  };

  const clearDuplicates = async () => {
    try {
      const r = await api.del(`doc-migration/source?folder=${enc}&onlyDuplicates=1`);
      toast.success(`Removed ${r.deleted} already-imported file${r.deleted === 1 ? '' : 's'} from the Dropbox folder.`);
      await loadDetail({ fresh: true });   // files just left the source tree
    } catch (e) { toast.error(e.message); }
  };

  const removeFolder = async () => {
    if (!window.confirm(`Delete the "${folder.sourceFolder}" folder${fileCount != null ? ` and its ${fileCount} file(s)` : ''} from the Dropbox folder in SharePoint? Imported documents already in the deal are NOT affected.`)) return;
    try {
      const r = await api.del(`doc-migration/source?folder=${enc}`);
      toast.success(`Removed “${folder.sourceFolder}” (${r.deleted} file${r.deleted === 1 ? '' : 's'}) from the Dropbox folder.`);
      await reloadList();   // the folder is gone — refresh the whole list
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Card style={{ marginBottom: space.md }}>
      {/* Header line */}
      <div style={{ display: 'flex', alignItems: 'center', gap: space.sm, flexWrap: 'wrap' }}>
        <button onClick={() => setExpanded(v => !v)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon name="chevronDown" size={15}
            style={{ color: B.textLt, transform: expanded ? 'rotate(0)' : 'rotate(-90deg)', transition: 'transform .15s' }} />
          <span style={{ fontWeight: weight.semibold, color: B.text }}>{folder.sourceFolder}</span>
        </button>
        {loadingDetail ? (
          <span style={{ color: B.textLt, fontSize: font.sm }}>Scanning…</span>
        ) : detailErr ? (
          <span style={{ color: B.red, fontSize: font.xs }}>
            {detailErr}{' '}
            <button onClick={() => loadDetail({ fresh: true })} style={{ background: 'none', border: 'none', color: B.navy, cursor: 'pointer', textDecoration: 'underline', padding: 0, fontSize: font.xs }}>retry</button>
            {' · '}
            {/* Copyable for the same reason the import error is: the server names
                the step and the cause, and that should not need devtools. */}
            <button onClick={() => navigator.clipboard?.writeText(
              JSON.stringify({ sourceFolder: folder.sourceFolder, scanError: detailErr }, null, 2))}
              style={{ background: 'none', border: 'none', color: B.navy, cursor: 'pointer', textDecoration: 'underline', padding: 0, fontSize: font.xs }}>
              copy details
            </button>
          </span>
        ) : (
          <>
            <span style={{ color: B.textLt, fontSize: font.sm }}>
              {partial ? `${fileCount}+ files so far` : `${fileCount} file${fileCount === 1 ? '' : 's'}`}
              {' · '}{humanSize(detail.totalBytes)}{partial ? '+' : ''}
            </span>
            {/* The counts describe what has been indexed, not the whole tree —
                say so, or a big folder silently looks smaller than it is. */}
            {partial && (
              <Badge color={B.amber}>
                {detail.partialReason
                  ? `Still reading — ${detail.partialReason}`
                  : 'Still reading this folder — counts are partial'}
                {partialTries.current > 0 && partialTries.current < MAX_PARTIAL_TRIES
                  ? ` (pass ${partialTries.current + 1})` : ''}
              </Badge>
            )}
            {isMapped && newCount > 0 && <Badge color={B.green}>{newCount} new</Badge>}
            {isMapped && dupCount > 0 && <Badge color={B.textLt}>{dupCount} already in app</Badge>}
            {!isMapped && <Badge color={B.amber}>Not mapped</Badge>}
          </>
        )}
      </div>

      {/* Mapping controls */}
      <div style={{ display: 'flex', gap: space.md, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: space.md }}>
        <div style={{ flex: '1 1 320px', minWidth: 260 }}>
          <InlineSearchPicker
            label="Maps to deal / detail"
            items={dealPickerItems}
            value={pickValue}
            onChange={(v) => { setPickValue(v); if (v) saveMapping(v, target); }}
            placeholder="Search deals…"
            clearLabel="— choose a deal —"
          />
        </div>
        <div style={{ flex: '0 1 260px', minWidth: 200 }}>
          <label style={{ display: 'block', fontSize: font.xs, color: B.textMd, marginBottom: 4, fontWeight: weight.medium }}>
            Target sub-folder (optional)
          </label>
          <input
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            onBlur={() => { if (isMapped) saveMapping(pickValue, target); }}
            placeholder="e.g. Legal/Purchase"
            style={{
              width: '100%', padding: '8px 10px', border: `1px solid ${B.border}`,
              borderRadius: radius.md, fontSize: font.sm, color: B.text, fontFamily: 'inherit',
            }}
          />
        </div>
        {isMapped && (
          <Btn variant="ghost" size="sm" onClick={clearMapping} disabled={saving}>Clear</Btn>
        )}
      </div>

      {isMapped && mapping.dealName && (
        <div style={{ marginTop: space.xs, fontSize: font.xs, color: B.textLt }}>
          → {mapping.dealName}{mapping.detailLabel ? ` · ${mapping.detailLabel}` : ''}
          {mapping.targetFolderPath ? ` · ${mapping.targetFolderPath}` : ''}
        </div>
      )}

      {/* Action row */}
      <div style={{ display: 'flex', gap: space.sm, alignItems: 'center', marginTop: space.md, flexWrap: 'wrap' }}>
        <Btn onClick={runImport} disabled={!isMapped || importing || loadingDetail || newCount === 0}>
          {importing ? 'Importing…' : newCount > 0 ? `Import ${newCount} new file${newCount === 1 ? '' : 's'}` : 'Nothing new to import'}
        </Btn>
        {runLive && (
          <span style={{ color: B.textMd, fontSize: font.sm }}>{runSummary}</span>
        )}
        {isMapped && dupCount > 0 && (
          <Btn variant="ghost" size="sm" onClick={clearDuplicates}>Clear {dupCount} already-imported</Btn>
        )}
        <div style={{ flex: 1 }} />
        <Btn variant="ghost" size="sm" onClick={removeFolder}>Remove from Dropbox</Btn>
      </div>

      {/* The run, while one is live. This is the whole point of moving the schedule
          server-side: the import is visible and survives the tab being closed. */}
      {runLive && (
        <div style={{ marginTop: space.sm, padding: space.sm, borderRadius: 6,
                      background: '#f8fafc', border: `1px solid ${B.border}` }}>
          <div style={{ display: 'flex', gap: space.sm, alignItems: 'baseline',
                        fontSize: font.xs, color: B.textMd }}>
            {/* A bare inline spinner — the shared <Spinner> is a centred block
                with xxxl padding and its own "Loading…" caption, which turns this
                one-line phase indicator into a large empty box. */}
            <span aria-hidden="true" style={{
              width: 12, height: 12, borderRadius: '50%', flex: '0 0 auto',
              border: `2px solid ${B.border}`, borderTopColor: B.navy,
              display: 'inline-block', animation: 'harvus-spin 0.7s linear infinite',
            }} />
            <strong style={{ color: B.text }}>
              {runPhase?.index ? `Step ${runPhase.index} of ${runPhase.total}: ` : ''}
              {runPhase?.label || 'Starting the import'}
            </strong>
            {runPhase?.detail && <span>{runPhase.detail}</span>}
            <div style={{ flex: 1 }} />
            <span>runs in the background — you can close this page</span>
          </div>
          {/* Never shows full while work continues; a full bar that keeps going
              reads as a hang. */}
          <div style={{ marginTop: 6, height: 6, borderRadius: 3, background: B.border,
                        overflow: 'hidden' }}>
            <div style={{ width: `${Math.round(runFrac * 100)}%`, height: '100%',
                          background: B.navy, transition: 'width .4s ease' }} />
          </div>
          {run.fileCount > 0 && (
            <div style={{ marginTop: 4, fontSize: font.xs, color: B.textLt }}>
              {run.ingested} imported · {run.skipped} already in app
              {run.failed > 0 ? ` · ${run.failed} failed` : ''} · {run.remaining} to go
            </div>
          )}
        </div>
      )}

      {/* Last import failure — persistent, and copyable in one click so the exact
          server diagnosis can be passed on without opening devtools. */}
      {importErr && (
        <div style={{ marginTop: space.sm, padding: space.sm, borderRadius: 6,
                      background: '#fef2f2', border: `1px solid ${B.red}`, fontSize: font.xs }}>
          <div style={{ display: 'flex', gap: space.sm, alignItems: 'baseline' }}>
            <strong style={{ color: B.red }}>Import failed</strong>
            {importErr.step && (
              <span style={{ color: B.textMd }}>while: <code>{importErr.step}</code></span>
            )}
            <div style={{ flex: 1 }} />
            <button
              onClick={() => navigator.clipboard?.writeText(
                JSON.stringify({ sourceFolder: folder.sourceFolder, ...importErr }, null, 2))}
              style={{ background: 'none', border: 'none', color: B.navy, cursor: 'pointer',
                       textDecoration: 'underline', padding: 0, fontSize: font.xs }}>
              copy details
            </button>
            <button onClick={() => setImportErr(null)}
              style={{ background: 'none', border: 'none', color: B.textLt, cursor: 'pointer',
                       padding: 0, fontSize: font.xs }}>
              dismiss
            </button>
          </div>
          <div style={{ marginTop: 4, color: B.text, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {importErr.rawError || importErr.message}
          </div>
        </div>
      )}

      {/* Files the run could not import. Shown rather than logged — an over-large
          file names itself here, which is the whole point of refusing it by name. */}
      {fileErrs.length > 0 && (
        <div style={{ marginTop: space.sm, padding: space.sm, borderRadius: 6,
                      background: '#fffbeb', border: `1px solid ${B.amber}`, fontSize: font.xs }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: space.sm }}>
            <strong style={{ color: B.text }}>
              {fileErrs.length} file{fileErrs.length === 1 ? '' : 's'} could not be imported
            </strong>
            <div style={{ flex: 1 }} />
            <button onClick={() => setFileErrs([])}
              style={{ background: 'none', border: 'none', color: B.textLt, cursor: 'pointer',
                       padding: 0, fontSize: font.xs }}>
              dismiss
            </button>
          </div>
          <ul style={{ margin: '4px 0 0 0', paddingLeft: 18, color: B.textMd }}>
            {fileErrs.slice(0, 20).map((e, i) => (
              <li key={i} style={{ wordBreak: 'break-word' }}>
                <strong>{e.file}</strong> — {e.error}
              </li>
            ))}
            {fileErrs.length > 20 && <li>…and {fileErrs.length - 20} more (see console).</li>}
          </ul>
        </div>
      )}

      {/* File list */}
      {expanded && (
        <div style={{ marginTop: space.md, borderTop: `1px solid ${B.divider}`, paddingTop: space.sm }}>
          {loadingDetail ? (
            <div style={{ color: B.textLt, fontSize: font.sm }}>Scanning this folder…</div>
          ) : detailErr ? (
            <div style={{ color: B.red, fontSize: font.sm }}>{detailErr}</div>
          ) : (detail.files || []).length === 0 ? (
            <div style={{ color: B.textLt, fontSize: font.sm }}>No files in this folder.</div>
          ) : (detail.files.map((file) => {
            const meta = STATUS_META[file.status] || STATUS_META.unmapped;
            return (
              <div key={file.itemId}
                style={{ display: 'flex', alignItems: 'center', gap: space.sm, padding: '4px 0', fontSize: font.sm }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: meta.color, flexShrink: 0 }} />
                <span style={{ color: B.text, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {file.subPath}
                </span>
                <span style={{ color: B.textLt, flexShrink: 0 }}>{humanSize(file.sizeBytes)}</span>
                <span style={{ color: meta.color, flexShrink: 0, fontSize: font.xs, fontWeight: weight.medium, minWidth: 90, textAlign: 'right' }}>
                  {meta.label}
                </span>
              </div>
            );
          }))}
        </div>
      )}
    </Card>
  );
}
